import React, { useEffect, useMemo, useState, memo } from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import ImageComponent from "../../../../Components/ImageComponent";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import TimerComponent from "../../../../Components/ongoing-common-components/general-components/TimerComponent";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import CreateEventTooltip from "../../../../Components/home-common-components/CreateEventTooltip";
import SaveChangesTooltip from "../UpComingEvents/SaveChangesTooltip";
import { format } from "date-fns";
import GameShowCardSkeleton from "../../../../Components/skeleton-components/GameShowCardSkeleton";
import RewardWagerSkeleton from "../../../../Components/skeleton-components/RewardWagerSkeleton";
import GameShowDetailSkeleton from "../../../../Components/skeleton-components/GameShowDetailSkeleton";
import PrivateEventTimerComponent from "../../../../Components/ongoing-common-components/general-components/PrivateEventTimerComponent";

interface EventDetailScreenCardProps {
  setIsMinAttendees: (value: boolean) => void;
  details: any;
  isMinAttendees: boolean;
  saveBtnTooltip?: boolean;
  setSaveBtnTooltip?: (value: boolean) => void;
  userData: {
    username?: string;
  };
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  setTimeLeft: (time: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }) => void;
  isStreamConnected?: string;
  setSelectedButton?: (value: string) => void;
  isMoreTimeLeft: boolean;
  setIsMoreTimeLeft: (value: boolean) => void;
  screenName?: any;
  hasCheckedIn?: boolean;
  IsEventCanceled: any;
  IsEventCompleted?: any;
  isLoading?: boolean;
  paddingOnTop?: boolean
}

const EventDetailScreenCard: React.FC<EventDetailScreenCardProps> = ({
  setIsMinAttendees,
  details,
  isMinAttendees,
  saveBtnTooltip,
  setSaveBtnTooltip,
  userData,
  timeLeft,
  setTimeLeft,
  isStreamConnected,
  hasCheckedIn,
  setSelectedButton,
  isMoreTimeLeft,
  setIsMoreTimeLeft,
  screenName,
  IsEventCanceled,
  IsEventCompleted,
  isLoading = false,
  paddingOnTop = true
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  console.log("isLoading", isLoading);

  const getStreamCreationStatus = () => {
    if (details?.liveStreamWithTwitch) {
      return userData?.username === details?.organiserId
        ? details?.organizerTwitchStream
        : details?.challengerTwitchStream;
    } else {
      return userData?.username === details?.organiserId
        ? details?.organizerYoutubeStream
        : details?.challengerYoutubeStream;
    }
  };

  const streamCreationStatus = getStreamCreationStatus();
  const signUpNumber = 70;
  const requiredNumber = details?.minimumAttendees
    ? Math.max(details.minimumAttendees - signUpNumber, 0)
    : 0;

  const totalPercentage = details?.minimumAttendees
    ? (signUpNumber * 100) / details.minimumAttendees
    : 0;

  const currentDate = new Date();

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (details && details.eventStartTime) {
        const eventDate = new Date(details.eventStartTime).getTime();
        const now = Date.now();
        const difference = eventDate - now;
        if (difference > 0) {
          setTimeLeft({
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
          });
        } else {
          setTimeLeft({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
          });
        }
      }
    };

    const startDate = new Date(details?.eventStartTime);
    const timeDifference = startDate.getTime() - currentDate.getTime();
    const hoursLeft = timeDifference / (1000 * 60 * 60);

    setIsMinAttendees(requiredNumber > signUpNumber);

    if (hoursLeft <= 1 && hoursLeft >= 0) {
      setIsMoreTimeLeft(false);
    } else {
      setIsMoreTimeLeft(true);
    }

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [details]);

  const formattedStake =
    details?.stakes
      ?.filter((stake) => stake.quantity > 0)
      ?.map((stake) => `${stake.quantity} ${stake.type}`)
      ?.join(", ") || "No Stakes";

  const medalsData = [
    { id: 0, name: "Coin", image: AppImages.icCoin, value: 0 },
    { id: 1, name: "Bronze", image: AppImages.icBronzeBelt, value: 0 },
    { id: 2, name: "Silver", image: AppImages.icSilverBelt, value: 0 },
    { id: 3, name: "Gold", image: AppImages.icGoldBelt, value: 0 },
    { id: 4, name: "Platinum", image: AppImages.icPlatinumBelt, value: 0 },
  ];

  const updatedMedals = medalsData
    .map((medal) => {
      const stake = details?.stakes?.find((stake) => stake.type === medal.name);
      return {
        ...medal,
        value: stake ? stake.quantity : 0,
      };
    })
    .filter((medal) => medal.value > 0);

  const date = useMemo(
    () => (details ? new Date(details?.eventStartTime) : new Date()),
    [details]
  );

  const formattedDate = details
    ? format(date, "EEEE, MMMM d, yyyy, 'at' h:mm")
    : "";

  const [isLessThanOneHour, setIsLessThanOneHour] = useState(false);

  useEffect(() => {
    const calculateIsLessThanOneHour = () => {
      const eventStartTime = new Date(details?.eventStartTime);
      const currentDate = new Date();
      const timeDifference = eventStartTime.getTime() - currentDate.getTime();
      const hoursLeft = timeDifference / (1000 * 60 * 60);

      setIsLessThanOneHour(hoursLeft <= 1);
    };

    calculateIsLessThanOneHour();
    const interval = setInterval(calculateIsLessThanOneHour, 1000);
    return () => clearInterval(interval);
  }, [details?.eventStartTime]);

  const currentTickets = 25;
  const totalTickets = 100;
  const progress = (currentTickets / totalTickets) * 100; // Percentage
  const ticketsNeeded = totalTickets - currentTickets;
  const eventName = "Public";

  return (
    <View
      style={[
        styles.gameshowOuterView,
        paddingOnTop ?{
          marginTop: 15,
        }:{},
      ]}
    >
      <LinearGradient
        colors={["rgba(23, 30, 40, 0.5)", "rgba(17, 24, 39, 0.50)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          padding:
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? 6
              : 20,
          borderRadius: 8,
          gap:
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? 12
              : 20,
        }}
      >
        <View
          style={[
            styles.deviceflexDirection,
            {
              position: "relative",
              justifyContent: "space-between",
            },
          ]}
        >
          {isLoading ? (
            <View
              style={{
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                overflow: "hidden",
                width:
                  deviceSize === DEVICE_SIZES.SM ||
                  deviceSize === DEVICE_SIZES.XS
                    ? "100%"
                    : 446,
              }}
            >
              <GameShowCardSkeleton />
            </View>
          ) : (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={{
                justifyContent: IsEventCanceled ? "space-evenly" : "center",
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
              }}
            >
              <View
                style={[
                  styles.countdownContainer,
                  {
                    width:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? "100%"
                        : 446,
                    maxWidth:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? "100%"
                        : 446,
                    minWidth:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? "100%"
                        : 446,
                    marginTop:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? 0
                        : 0,
                  },
                ]}
              >
                {details?.eventType === "Private" ? (
                  <PrivateEventTimerComponent
                    isMoreTimeLeft={isMoreTimeLeft}
                    isMinAttendees={isMinAttendees}
                    timeLeft={timeLeft}
                    requiredNumber={requiredNumber}
                    totalPercentage={totalPercentage}
                    showTimer={false}
                    formattedDate={formattedDate}
                    title={details ? details.eventTitle : ""}
                    IsEventCanceled={IsEventCanceled}
                    IsEventCompleted={IsEventCompleted}
                  />
                ) : (
                  <TimerComponent
                    isMoreTimeLeft={isMoreTimeLeft}
                    isMinAttendees={isMinAttendees}
                    timeLeft={timeLeft}
                    requiredNumber={requiredNumber}
                    totalPercentage={totalPercentage}
                    showTimer={false}
                    formattedDate={formattedDate}
                    title={details ? details.eventTitle : ""}
                    type="EventDetail"
                    IsEventCanceled={IsEventCanceled}
                    IsEventCompleted={IsEventCompleted}
                  />
                )}
              </View>
              {IsEventCanceled && (
                <View
                  style={{
                    width:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? "100%"
                        : 446,
                  }}
                >
                  <Text
                    style={{
                      ...AppFontStyle.BOLD_ANYBODY_12,
                      textAlign: "center",
                    }}
                  >
                    We’re sorry to inform you that {details?.eventTitle} has
                    been canceled due to {details?.reason}. If you purchased a
                    ticket, your refund will be processed in tokens shortly.
                  </Text>
                </View>
              )}
            </LinearGradient>
          )}
          {isLoading ? (
            <View
              style={{
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                overflow: "hidden",
                marginTop:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 10
                    : 0,
              }}
            >
              <View
                style={{
                  padding:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 12
                      : 20,
                }}
              >
                <GameShowDetailSkeleton />
              </View>
            </View>
          ) : (
            <View style={styles.eventDetailsMainContainer}>
              <View style={styles.eventImageContainer}>
                <ImageComponent
                  url={
                    details?.eventImageUrl
                      ? details.eventImageUrl
                      : AppImages.KO_LOGO_Main
                  }
                  style={styles.eventImage}
                />
              </View>
              <View style={styles.eventInfoGrid}>
                <View style={styles.eventInfoItem}>
                  <Text style={styles.eventInfoLabel}>Game</Text>
                  <Text style={styles.eventInfoValue}>
                    {details ? details.gameTitle : ""}
                  </Text>
                </View>
                <View style={styles.eventInfoItem}>
                  <Text style={styles.eventInfoLabel}>Event type</Text>
                  <Text style={styles.eventInfoValue}>
                    {details?.type === "Private" ? "Exclusive" : "Kall Out"}
                  </Text>
                </View>
                <View style={styles.eventInfoItem}>
                  <Text style={styles.eventInfoLabel}>Event Title</Text>
                  <Text style={styles.eventInfoValue}>
                    {details ? details?.eventTitle : ""}
                  </Text>
                </View>
                <View style={styles.eventInfoItem}>
                  <Text style={styles.eventInfoLabel}>solo</Text>
                  <Text style={styles.eventInfoValue}>
                    {details?.challengeMode ? "1 VS 1" : "1 VS 1"}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {isLoading ? (
            <View
              style={{
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                overflow: "hidden",
                marginTop:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 10
                    : 0,
              }}
            >
              <View
                style={{
                  padding:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 12
                      : 15,
                }}
              >
                <RewardWagerSkeleton />
              </View>
            </View>
          ) : (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={{
                justifyContent: "space-between",
                marginTop:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 10
                    : 0,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                padding: 15,
                minWidth:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? "100%"
                    : 242,
                maxWidth:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? "100%"
                    : 242,
                flexShrink: 0,
              }}
            >
              <View style={{ alignItems: "center", zIndex: -1 }}>
                <View
                  style={{
                    width:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? "100%"
                        : 212,
                    alignItems: "center",
                  }}
                >
                  <Text style={AppFontStyle.MEDIUM_ANYBODY_12}>
                    This challenge is for 2 Bronze Belts:
                    <Text style={AppFontStyle.BOLD_ANYBODY_16}>
                      worth 120 credits
                    </Text>
                  </Text>
                </View>
              </View>

              <View
                style={{
                  width:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? "100%"
                      : 212,
                  alignItems: "center",
                  zIndex: -1,
                }}
              >
                <View
                  style={{
                    padding: 10,
                    backgroundColor: AppColors.black,
                    borderRadius: 10,
                    marginTop: 10,
                  }}
                >
                  <Text style={AppFontStyle.BOLD_ANYBODY_22}>{`$${
                    details ? details?.totalBalance : 0
                  }`}</Text>
                </View>
              </View>

              <View style={{ zIndex: -1 }}>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_16,
                    { marginVertical: 10 },
                  ]}
                >
                  Wager
                </Text>

                <View
                  style={{ flexDirection: "row", alignItems: "flex-start" }}
                >
                  {updatedMedals.map((medal) => (
                    <View
                      key={medal.id}
                      style={{ alignItems: "center", marginRight: 10 }}
                    >
                      <Image
                        source={medal.image}
                        style={{
                          width: 50,
                          height: 50,
                          resizeMode: "contain",
                        }}
                      />
                      <Text style={AppFontStyle.BOLD_ANYBODY_18}>
                        {medal.value}
                      </Text>
                      <Text style={AppFontStyle.REGULAR_ANYBODY_12}>
                        {medal.name}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </LinearGradient>
          )}
        </View>
        {details?.eventType === "Private" && (
          <View style={{ alignItems: "flex-start", gap: 12 }}>
            <View style={{ width: "100%" }}>
              <View style={styles.progressTextContainer}>
                <Text style={styles.progressText}>{currentTickets}</Text>
                <Text style={styles.progressText}>{totalTickets}</Text>
              </View>

              {/* Progress Bar */}
              <View style={styles.progressBar}>
                <View
                  style={[styles.progressFill, { width: `${progress}%` }]}
                />
              </View>
            </View>
            <View
              style={{
                backgroundColor: AppColors.blue_B0DEFF,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: AppColors.green_A5D6A7,
                padding: 8,
              }}
            >
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  {
                    color: AppColors.gray_424242,
                  },
                ]}
              >
                This event needs [X] more tickets to go live! Be part of the
                action – grab your ticket now!
              </Text>
            </View>
          </View>
        )}
      </LinearGradient>
    </View>
  );
};

export default memo(EventDetailScreenCard);

const useStyles = CreateResponsiveStyle(
  {
    countdownContainer: {
      borderRadius: 8,
      padding: 20,
      overflow: "hidden",
    },
    addDescriptionView: {
      width: 603,
    },
    eventDetailsContainer: {
      alignSelf: "flex-end",
      marginVertical: 36,
      flex: 1,
    },
    eventDetailsMainContainer: {
      justifyContent: "center",
      flexDirection: "row",
      gap: 10,
      flex: 1,
      maxWidth: 500,
      minWidth: 0,
    },
    eventImageContainer: {
      justifyContent: "center",
    },
    eventImage: {
      width: 241,
      maxWidth: 241,
      height: 218,
      resizeMode: "fill",
      borderRadius: 8,
      flexShrink: 0,
    },
    eventInfoGrid: {
      gap: 12,
      marginLeft: 10,
      justifyContent: "center",
      paddingRight: 10,
      flex: 1,
      minWidth: 0,
    },
    eventInfoItem: {
      flexShrink: 1,
      minWidth: 0,
    },
    eventInfoLabel: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      color: AppColors.gray_C3C3C3,
      marginVertical: 10,
      
    },
    eventInfoValue: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      flexWrap: "wrap",
      flexShrink: 1,
    },
    gameshowOuterView: {
      borderRadius: 8,
      borderWidth: 0.8,
      borderColor: "#374151",
      overflow: "hidden",
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      margin: 10,
      borderRadius: 7,
      width: 212,
    },
    warnBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.red_AF0707,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 450,
      top: -177,
      width: 234,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    optionBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.appGreenColor_51AF07,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 350,
      top: -177,
      width: 94,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    allBtns: {
      position: "absolute",
      right: 0,
    },
    streamBtnView: {},
    deviceflexDirection: {
      flexDirection: "row",
      gap: 10,
      flexWrap: "nowrap",
      alignItems: "flex-start",
    },
    progressTextContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 4,
    },
    progressText: {
      ...AppFontStyle.REGULAR_ANYBODY_12,
      color: AppColors.gray_C4C4C4,
    },
    progressBar: {
      height: 12,
      backgroundColor: AppColors.white,
      borderRadius: 10,
      overflow: "hidden",
      width: 384,
    },
    progressFill: {
      height: "100%",
      backgroundColor: AppColors.green_00B536,
      borderRadius: 10,
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      countdownContainer: {
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
        overflow: "hidden",
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "flex-start",
      },
      gameshowOuterView: {
        margin: 5,
        borderRadius: 8,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.LG]: {
      countdownContainer: {
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
        overflow: "hidden",
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
      },
      gameshowOuterView: {
        margin: 5,
        borderRadius: 8,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.SM]: {
      countdownContainer: {
        borderRadius: 8,
        paddingVertical: 15,
        paddingHorizontal: 12,
        minHeight: 160,
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        overflow: "hidden",
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        borderRadius: 10,
        alignSelf: "flex-end",
        marginVertical: 15,
      },
      eventDetailsMainContainer: {
        flexDirection: "column",
        gap: 12,
        marginBottom: 0,
      },
      eventImageContainer: {
        width: "100%",
        alignItems: "center",
        marginTop: 12,
      },
      eventImage: {
        width: "100%",
        height: 180,
        resizeMode: "cover",
      },
      eventInfoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        marginLeft: 0,
        paddingRight: 0,
        paddingTop: 8,
        gap: 0,
      },
      eventInfoItem: {
        width: "47%",
        marginBottom: 12,
      },
      eventInfoLabel: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: AppColors.gray_C3C3C3,
        marginBottom: 4,
        marginTop: 0,
      },
      eventInfoValue: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        marginTop: 0,
      },
      gameshowOuterView: {
        margin: 5,
        borderRadius: 8,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "50%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
      progressBar: { width: "100%" },
    },
    [DEVICE_SIZES.XS]: {
      progressBar: { width: "100%" },
      countdownContainer: {
        borderRadius: 8,
        paddingVertical: 15,
        paddingHorizontal: 12,
        minHeight: 160,
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        overflow: "hidden",
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
        marginVertical: 15,
      },
      eventDetailsMainContainer: {
        flexDirection: "column",
        gap: 12,
        marginBottom: 0,
      },
      eventImageContainer: {
        width: "100%",
        alignItems: "center",
        marginTop: 12,
      },
      eventImage: {
        width: "100%",
        height: 180,
        resizeMode: "cover",
      },
      eventInfoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        marginLeft: 0,
        paddingRight: 0,
        paddingTop: 8,
        gap: 0,
      },
      eventInfoItem: {
        width: "47%",
        marginBottom: 12,
      },
      eventInfoLabel: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: AppColors.gray_C3C3C3,
        marginBottom: 4,
        marginTop: 0,
      },
      eventInfoValue: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        marginTop: 0,
      },
      gameshowOuterView: {
        margin: 5,
        borderRadius: 8,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "60%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
    },
  }
);
